<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useUsersStore } from '@/presentation/stores/users/users.ts'
import { storeToRefs } from 'pinia'
import { UserRole } from '@/core/domain/resources/enums/UserRole.ts'
import { UserGender } from '@/core/domain/resources/enums/UserGender.ts'
import Pair from '@/core/domain/resources/types/Pair.ts'
import { type UserAdminView } from '@/core/domain/users/LocalUser.ts'
import { DateTime } from 'luxon'
import { useRouter } from 'vue-router'
import { type UserSearchRequestEntity } from '@/core/infrastructure/schemas/users/core/UserInputSchema.ts'
import UserSearchRequest from '@/core/domain/users/UserSearchRequest.ts'
import CUserDataTable from '@/presentation/components/users/CUserDataTable.vue'
import type { UserDataTableNamespace } from '@/presentation/components/users/utils/UserDataTableType.ts'
import type { DataTableSortEvent, DataTableSortMeta } from 'primevue/datatable'

// Composables
const usersStore = useUsersStore()
const router = useRouter()

// Store refs
const { usersStoreError, getTotal: total, getPageSize: pageSize } = storeToRefs(usersStore)

// Refs / Constantes / Computed
const CHUNK: number | undefined = 50
const BLOCK = computed(() => CHUNK ?? pageSize.value)

// total connu par le VirtualScroller
const totalRecords = computed(() => total.value ?? 0)

// Buffer « sparse »
const buffer = ref<Array<UserDataTableNamespace.UserDataTableRow | undefined>>([])
const idByIndex = ref<Record<string, number>>({})
const loading = ref(false)
const filtersLoading = ref(false)

// Set technique pour la gestion des pages en cours de chargement
const loadedPages = new Set<number>()
const pendingPages = new Set<number>()

// Sort pour la pagination
const sortField = ref<string[]>(['id:asc'])

// Filters
const filters = ref<UserSearchRequestEntity>({
  q: null,
  roles: [],
  genders: [],
  blocked: null,
  emailVerified: null
})

// Fonction de traitement des actions.
const processAfterAction = (
  u: UserDataTableNamespace.UserDataTableRow,
  summary: string,
  successMsg: string,
  errorMsg: string
) => {
  if (usersStoreError.value.hasError) {
    usersStore.showErrorToast(summary, errorMsg, 5000)
  } else {
    const i = idByIndex.value[u.identifier]
    buffer.value[i] = convertUser(usersStore.getUserByIdentifier(u.identifier)!!)
    usersStore.showSuccessToast(summary, successMsg)
  }
}

const userEdit = (u: UserDataTableNamespace.UserDataTableRow) =>
  router.push({ name: 'edit-user', params: { identifier: u.identifier } })

const userRefresh = async (u: UserDataTableNamespace.UserDataTableRow) => {
  await usersStore.fetchByIdentifier(u.identifier, false)
  processAfterAction(
    u,
    'Rafraîchissement des données',
    `L'utilisateur ${u.fullName} a été actualisé !`,
    usersStoreError.value.message
  )
}

const resetPassword = async (u: UserDataTableNamespace.UserDataTableRow) => {
  const res = await usersStore.resetPassword(u.identifier)
  if (res === undefined) {
    usersStore.showErrorToast(
      'Reset du mot de passe',
      'Une erreur est survenue, le mot de passe ne peut pas être modifié.'
    )
    return
  }
  processAfterAction(
    u,
    'Reset du mot de passe',
    `Le mot de passe de l'utilisateur ${u.fullName} a été réinitialisé !`,
    usersStoreError.value.message
  )
  usersStore.showToast({severity: 'success', summary: `Mot de passe de ${u.fullName}`, detail:  res })
}

const verifyEmail = async (u: UserDataTableNamespace.UserDataTableRow) => {
  await usersStore.verifyUserEmail(u.identifier)
  processAfterAction(
    u,
    'Validation du mail',
    `L'email de ${u.fullName} a été validé !`,
    usersStoreError.value.message
  )
}

const sendEmailResetPassword = async (u: UserDataTableNamespace.UserDataTableRow) => {
  await usersStore.sendEmailResetPassword(u.identifier)
  processAfterAction(
    u,
    'Réinitialisation du mot de passe',
    `L'email a bien été envoyé !`,
    usersStoreError.value.message
  )
}

const blockUser = async (u: UserDataTableNamespace.UserDataTableRow) => {
  await usersStore.blockUser(u.identifier)
  processAfterAction(
    u,
    'Blocage utilisateur',
    `L'utilisateur ${u.fullName} a été bloqué !`,
    usersStoreError.value.message
  )
}

const unblockUser = async (u: UserDataTableNamespace.UserDataTableRow) => {
  await usersStore.unblockUser(u.identifier)
  processAfterAction(
    u,
    'Déblocage utilisateur',
    `L'utilisateur ${u.fullName} a été débloqué !`,
    usersStoreError.value.message
  )
}

const deleteUser = async () => {
  usersStore.showInfoToast(
    'Suppression utilisateur',
    'La suppression est désactivée pour le moment. Veuillez-vous rapprocher du service technique',
    7500
  )
}

/**
 * Convertit un objet FullUser en objet RowUser
 * @param user
 */
function convertUser(user: UserAdminView): UserDataTableNamespace.UserDataTableRow {
  const row: UserDataTableNamespace.UserDataTableRow = {
    identifier: user.identifier,
    firstname: user.firstname,
    lastname: user.lastname,
    fullName: `${user.firstname} ${user.lastname}`,
    username: user.username,
    roles: Pair.of(
      user.roles,
      UserRole.sort(user.roles).map((r) => UserRole.label(r))
    ),
    picture: user.picture,
    email: user.email,
    gender: Pair.of(user.gender, UserGender.label(user.gender)),
    blocked: user.blocked,
    emailVerified: user.emailVerified,
    lastLogin: user.lastLogin
      ? DateTime.fromISO(user.lastLogin).toLocaleString(DateTime.DATETIME_SHORT)
      : '-',
    loginCount: user.loginCount ?? 0,
    createdAt: user?.createdAt,
    modifiedAt: user?.modifiedAt,
    actions: {} as UserDataTableNamespace.UserAction
  }
  row.actions = {
    edit: {
      label: 'Modifier',
      icon: 'pi pi-pencil',
      handler: userEdit,
      disabled: false,
      hidden: false
    },
    refresh: {
      label: 'Rafraîchir',
      icon: 'pi pi-refresh',
      handler: userRefresh,
      disabled: false,
      hidden: false
    },
    resetPassword: {
      label: 'Réinitialiser le mot de passe',
      icon: 'pi pi-key',
      handler: resetPassword,
      disabled: false,
      hidden: false
    },
    sendEmailResetPassword: {
      label: 'Envoyer l’e-mail de réinit. mot de passe',
      icon: 'pi pi-send',
      handler: sendEmailResetPassword,
      disabled: false,
      hidden: false
    },
    verifyEmail: {
      label: 'Vérifier l’email',
      icon: 'pi pi-envelope',
      handler: verifyEmail,
      disabled: false,
      hidden: row.emailVerified
    },
    blocked: {
      label: 'Bloquer',
      icon: 'pi pi-lock',
      handler: blockUser,
      disabled: false,
      hidden: row.blocked
    },
    unblocked: {
      label: 'Débloquer',
      icon: 'pi pi-lock-open',
      handler: unblockUser,
      disabled: false,
      hidden: !row.blocked
    },
    delete: {
      label: 'Supprimer',
      icon: 'pi pi-trash',
      handler: deleteUser,
      disabled: false,
      hidden: false
    }
  }
  return row
}

function clearCache() {
  usersStore.clearCache()
  idByIndex.value = {}
}

/**
 * Déclencheur de l'appel de la fonction de filtrage
 */
async function applyFilters() {
  try {
    filtersLoading.value = true
    clearCache()
    await loadData(0, BLOCK.value)
  } finally {
    filtersLoading.value = false
  }
}

/**
 * Application de l'ordre et actualisation du Buffer
 * @param event
 */
async function onSort(event: DataTableSortEvent) {
  let fields = ['id:asc']
  if (event.multiSortMeta && event.multiSortMeta.length > 0) {
    fields = event.multiSortMeta?.map(
      (meta: DataTableSortMeta) => `${meta.field}:${meta.order == -1 ? 'desc' : 'asc'}`
    ) ?? ['id:asc']
  }
  sortField.value = fields
  await applyFilters()
}

/**
 * Actualisation du Buffer si "totalRecords" change
 */
watch(
  totalRecords,
  (n) => {
    buffer.value = Array<UserDataTableNamespace.UserDataTableRow | undefined>(n)
  },
  { immediate: true }
)

/**
 * Délégation de chargement des données et hydratation du buffer
 */
async function loadData(offset: number, limit: number) {
  const req = UserSearchRequest.create(filters.value)
  await usersStore.fetchByOffset(req, offset, limit, sortField.value)

  // Hydrater le buffer pour la page chargée
  const pageStart = offset
  const pageEnd = Math.min(offset + limit, totalRecords.value)
  for (let i = pageStart; i < pageEnd; i++) {
    const u = usersStore.getItem(i)
    if (u) {
      const row = convertUser(u)
      if (buffer.value[i])
        Object.assign(buffer.value[i] as UserDataTableNamespace.UserDataTableRow, row)
      else buffer.value[i] = row
      idByIndex.value[u.identifier] = i
    }
  }
}

/**
 * Chargement de la page, initialisation du premier bloc
 */
onMounted(async () => {
  clearCache()
  if (totalRecords.value === 0) {
    await loadData(0, BLOCK.value)
  }
})

/**
 * Chargement lazy déclenché par VirtualScroller
 * @param event
 */
async function onLazyLoad(event: UserDataTableNamespace.UserDataTableLazyEvent) {
  const start = Math.max(0, event.first ?? 0)
  const lastExclusive = Math.min(event.last ?? start, totalRecords.value)
  if (lastExclusive <= start) return

  // Calcul des pages à couvrir
  const startPage = Math.floor(start / BLOCK.value)
  const endPage = Math.floor((lastExclusive - 1) / BLOCK.value)

  const pagesToLoad: number[] = []
  for (let p = startPage; p <= endPage; p++) {
    if (!loadedPages.has(p) && !pendingPages.has(p)) {
      pendingPages.add(p)
      pagesToLoad.push(p)
    }
  }
  if (!pagesToLoad.length) return

  loading.value = true
  try {
    // charge les pages en parallèle
    await Promise.all(
      pagesToLoad.map(async (page) => {
        const offset = page * BLOCK.value
        const limit = Math.min(BLOCK.value, totalRecords.value - offset)

        await loadData(offset, limit)
        loadedPages.add(page)
        pendingPages.delete(page)
      })
    )
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <c-user-data-table
    :buffer="buffer"
    :total-records="totalRecords"
    :filters="filters"
    :apply-filters="applyFilters"
    :filters-loading="filtersLoading"
    :on-sort="onSort"
    :on-lazy-loading="onLazyLoad"
  />
</template>